/**
 * KinsCircle Phase 6D — Family Documentary Card.
 *
 * STABILITY.md §13–§14:
 *   - READ-ONLY narrative card built from `buildFamilyJourney()`.
 *   - Renders null when `familyDocumentary` flag is OFF or no qualifying
 *     journey exists. Every panel cites memory counts + year ranges.
 *   - Tapping "Watch the documentary" opens StoryPlaybackModal with a
 *     chronological summary of the user's whole family story — slide
 *     content drawn directly from existing captions.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Film,
  PlayCircle,
  History,
  Sparkles,
  Layers,
} from 'lucide-react';
import {
  isFamilyDocumentaryEnabled,
  isStoryPlaybackEnabled,
} from '@/lib/ai/featureFlag';
import {
  buildFamilyJourney,
  buildLegacyTimeline,
  type FamilyJourney,
} from '@/lib/ai/legacyTimelineEngine';
import StoryPlaybackModal, { type PlaybackStory } from './StoryPlaybackModal';

const FamilyDocumentaryCard: React.FC = () => {
  if (!isFamilyDocumentaryEnabled()) return null;

  const journey = useMemo<FamilyJourney | null>(
    () => buildFamilyJourney(),
    [],
  );
  const [playOpen, setPlayOpen] = useState(false);

  if (!journey || !journey.totals.memoryCount) return null;

  const playStory = useMemo<PlaybackStory | null>(() => {
    if (!isStoryPlaybackEnabled()) return null;
    const t = buildLegacyTimeline(undefined, { maxSlidesPerTimeline: 14 });
    if (!t) return null;
    return {
      label: 'Your Family Documentary',
      subtitle:
        t.yearRange.from && t.yearRange.to
          ? `Based on ${t.memoryCount} memories from ${t.yearRange.from}–${t.yearRange.to}`
          : `Based on ${t.memoryCount} memor${t.memoryCount === 1 ? 'y' : 'ies'}`,
      slides: t.slides,
    };
  }, []);

  const span =
    journey.totals.yearRange.from && journey.totals.yearRange.to
      ? `${journey.totals.yearRange.from}–${journey.totals.yearRange.to}`
      : 'across your saved memories';

  return (
    <section className="py-12 px-4 bg-gradient-to-br from-violet-50/60 via-white to-fuchsia-50/40">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Film className="w-5 h-5 text-violet-700" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Your Family Documentary
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
            Beta
          </span>
        </div>

        <p className="text-base text-slate-700 mb-6 max-w-3xl leading-relaxed">
          {journey.headline}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <Card className="bg-white border border-violet-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <History className="w-4 h-4 text-violet-600" />
                Eras
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {journey.eras.length ? (
                journey.eras.slice(0, 3).map((e) => (
                  <div
                    key={e.era + e.yearRange.from}
                    className="text-xs text-slate-700"
                  >
                    <div className="font-semibold text-slate-900">
                      {e.era}{' '}
                      <span className="font-normal text-slate-500">
                        ({e.yearRange.from}–{e.yearRange.to})
                      </span>
                    </div>
                    <div className="text-[11px] italic text-slate-500">
                      Based on {e.count} memor{e.count === 1 ? 'y' : 'ies'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-slate-400">
                  Add a few more dated memories to surface eras.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-violet-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-fuchsia-500" />
                Standout Moments
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {journey.milestones.length ? (
                journey.milestones.slice(0, 4).map((m) => (
                  <div key={m.memoryId} className="text-xs text-slate-700">
                    <div className="font-semibold text-slate-900">
                      {m.label}{' '}
                      {m.year && (
                        <span className="font-normal text-slate-500">
                          ({m.year})
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-slate-400">
                  No standout milestones detected yet.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border border-violet-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-pink-500" />
                Themes
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {journey.clusters.length ? (
                <div className="flex flex-wrap gap-1">
                  {journey.clusters.slice(0, 6).map((c) => (
                    <Badge
                      key={c.id}
                      variant="secondary"
                      className="bg-violet-50 text-violet-700 border border-violet-200 text-[10px]"
                    >
                      {c.label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-400">
                  Themes appear once you have a few overlapping memories.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {isStoryPlaybackEnabled() && playStory && (
          <Button
            onClick={() => setPlayOpen(true)}
            size="lg"
            className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 hover:opacity-90 text-white"
          >
            <PlayCircle className="w-5 h-5 mr-2" />
            Watch the documentary
          </Button>
        )}

        <p className="text-[11px] text-slate-400 mt-4">
          Documentary content is drawn only from memories you saved
          {span ? ` (${span})` : ''}. Nothing about feelings, health, or events is
          inferred.
        </p>
      </div>

      <StoryPlaybackModal
        open={playOpen}
        onOpenChange={setPlayOpen}
        story={playStory}
      />
    </section>
  );
};

export default FamilyDocumentaryCard;
